<script setup lang="ts">
  import { useDialogA11y } from '@/composables/useDialogA11y'
  import { calendarSupportsParticipantInvites } from '@/composables/useProviderCapabilities'
  import { useTranslation } from '@/composables/useTranslation'
  import { CalendarEventTypeEnum } from '@/types/generated'
  import type { CustomerCalendar } from '@/types/generated'
  import type { CalendarGridEvent } from '@/types/calendar/grid'
  import { participantDisplayLabel } from '@/types/calendar/participants'
  import { calendarDisplayLabel } from '@/utils/calendar/display'
  import type { CalendarEventTypeLabels } from '@/utils/calendar/event-display'
  import { computed } from 'vue'
  import { RouterLink } from 'vue-router'

  const { t } = useTranslation({
    de: {
      termin: 'Termin',
      frist: 'Frist',
      wiedervorlage: 'Wiedervorlage',
      aufgabe: 'Aufgabe',
      generalAppointment: 'Allgemeiner Termin',
      courtAppointment: 'Gerichtstermin',
      privateAppointment: 'Privat',
      caseReference: 'Aktenzeichen',
      subject: 'Wegen',
      location: 'Ort / Adresse',
      owner: 'Sachbearbeiter:in',
      assistants: 'Assistenzen',
      keywords: 'Schlagworte',
      participants: 'Teilnehmer:innen',
      rsvpAccepted: 'Zugesagt',
      rsvpDeclined: 'Abgesagt',
      rsvpTentative: 'Vorbehalt',
      rsvpPending: 'Ausstehend',
      notes: 'Notizen',
      close: 'Schließen',
      edit: 'Bearbeiten',
      cancelEvent: 'Absagen'
    },
    en: {
      termin: 'Appointment',
      frist: 'Deadline',
      wiedervorlage: 'Reschedule',
      aufgabe: 'Task',
      generalAppointment: 'General appointment',
      courtAppointment: 'Court hearing',
      privateAppointment: 'Private',
      caseReference: 'File reference',
      subject: 'Subject',
      location: 'Location / address',
      owner: 'Case owner',
      assistants: 'Assistants',
      keywords: 'Keywords',
      participants: 'Participants',
      rsvpAccepted: 'Accepted',
      rsvpDeclined: 'Declined',
      rsvpTentative: 'Tentative',
      rsvpPending: 'Pending',
      notes: 'Notes',
      close: 'Close',
      edit: 'Edit',
      cancelEvent: 'Cancel event'
    }
  })

  const props = defineProps<{
    open: boolean
    event: CalendarGridEvent | null
    calendars: CustomerCalendar[]
    eventTypeLabels: CalendarEventTypeLabels
    allDayLabel: string
    caseHref: string | null
    canEdit?: boolean
    deleting?: boolean
  }>()

  const emit = defineEmits<{
    close: []
    edit: []
    delete: []
  }>()

  const requestClose = (): void => {
    emit('close')
  }

  const { dialogRef, onDialogKeydown } = useDialogA11y(
    computed(() => props.open && Boolean(props.event)),
    requestClose
  )

  const selectedCalendar = computed(() => {
    if (!props.event) {
      return undefined
    }

    return props.calendars.find((item) => item.uuid === props.event?.calendarId)
  })

  const calendarLabel = computed(() =>
    selectedCalendar.value ? calendarDisplayLabel(selectedCalendar.value) : ''
  )

  const showParticipantRsvp = computed(() => {
    if (props.event?.participantInvitesSupported === false) {
      return false
    }
    if (props.event?.participantInvitesSupported === true) {
      return true
    }
    return calendarSupportsParticipantInvites(selectedCalendar.value)
  })

  const caseLabel = computed(() => {
    if (!props.event) {
      return ''
    }

    if (props.event.caseNumber && props.event.caseTitle) {
      return `${props.event.caseNumber} — ${props.event.caseTitle}`
    }

    return props.event.caseNumber || props.event.caseTitle || ''
  })

  const eventTypeLabel = computed(() => {
    if (!props.event) {
      return ''
    }

    if (props.event.type === CalendarEventTypeEnum.GERICHTSTERMIN) {
      return t('courtAppointment')
    }

    if (props.event.type === CalendarEventTypeEnum.PRIVAT) {
      return t('privateAppointment')
    }

    if (props.event.type === CalendarEventTypeEnum.TERMIN) {
      return t('generalAppointment')
    }

    return props.eventTypeLabels[props.event.type]
  })

  const formatTimeRange = computed(() => {
    if (!props.event) {
      return ''
    }

    const start = new Date(props.event.start)
    const end = new Date(props.event.end)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return ''
    }

    const dateFmt = new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    })
    const timeFmt = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' })

    if (props.event.allDay) {
      return `${dateFmt.format(start)} · ${props.allDayLabel}`
    }

    const sameDay =
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate()

    if (sameDay) {
      return `${dateFmt.format(start)} · ${timeFmt.format(start)}-${timeFmt.format(end)}`
    }

    return `${dateFmt.format(start)} ${timeFmt.format(start)} → ${dateFmt.format(end)} ${timeFmt.format(end)}`
  })

  const requestEdit = (): void => {
    if (!props.deleting) {
      emit('edit')
    }
  }

  const externalParticipants = computed(() => props.event?.participantsExternalDetails ?? [])

  const internalParticipantNames = computed(() => props.event?.participantsInternal ?? [])

  const rsvpLabel = (state?: string): string => {
    switch (state) {
      case 'accepted':
        return t('rsvpAccepted')
      case 'declined':
        return t('rsvpDeclined')
      case 'tentative':
        return t('rsvpTentative')
      default:
        return t('rsvpPending')
    }
  }
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open && event"
      class="fixed inset-0 z-[85] flex items-center justify-center bg-black/35 p-4 backdrop-blur-[1px]"
      @click.self="requestClose"
    >
      <div
        ref="dialogRef"
        role="dialog"
        aria-modal="true"
        aria-labelledby="calendar-event-detail-title"
        aria-describedby="calendar-event-detail-description"
        tabindex="-1"
        class="calendar-main border-card-line relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden
          rounded-xl border bg-background shadow-2xl"
        @keydown="onDialogKeydown"
      >
        <button
          type="button"
          :aria-label="t('close')"
          class="text-muted-foreground-1 hover:bg-muted-hover focus:outline-hidden absolute end-2.5 top-2.5 z-10
            inline-flex size-7 items-center justify-center rounded-md"
          @click="requestClose"
        >
          <svg
            class="size-4 shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>

        <div class="grow overflow-y-auto px-5 py-5">
          <div class="space-y-5">
            <div>
              <h3
                id="calendar-event-detail-title"
                class="text-lg font-semibold text-foreground"
              >
                {{ event.title }}
              </h3>
              <p
                id="calendar-event-detail-description"
                class="text-muted-foreground-1 mt-1 text-sm"
              >
                {{ formatTimeRange }}
              </p>
              <div class="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                <span
                  v-if="calendarLabel"
                  class="bg-surface inline-flex items-center gap-x-1.5 rounded-md px-2 py-0.5 text-foreground"
                >
                  <svg
                    class="size-3 shrink-0"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect
                      width="18"
                      height="18"
                      x="3"
                      y="4"
                      rx="2"
                    />
                    <path d="M16 2v4" />
                    <path d="M8 2v4" />
                    <path d="M3 10h18" />
                  </svg>
                  {{ calendarLabel }}
                </span>
                <span
                  v-if="eventTypeLabel"
                  class="bg-surface inline-flex items-center rounded-md px-2 py-0.5 text-foreground"
                >
                  {{ eventTypeLabel }}
                </span>
              </div>
            </div>

            <dl class="grid grid-cols-1 gap-x-6 gap-y-4 text-sm sm:grid-cols-2">
              <div v-if="caseLabel">
                <dt class="text-muted-foreground-2 mb-0.5 text-xs font-medium">
                  {{ t('caseReference') }}
                </dt>
                <dd class="text-foreground">
                  <RouterLink
                    v-if="caseHref"
                    :to="caseHref"
                    class="text-primary hover:underline"
                    @click="requestClose"
                  >
                    {{ caseLabel }}
                  </RouterLink>
                  <span v-else>{{ caseLabel }}</span>
                </dd>
              </div>
              <div v-if="event.wegen">
                <dt class="text-muted-foreground-2 mb-0.5 text-xs font-medium">
                  {{ t('subject') }}
                </dt>
                <dd class="text-foreground">{{ event.wegen }}</dd>
              </div>
              <div v-if="event.locationText">
                <dt class="text-muted-foreground-2 mb-0.5 text-xs font-medium">
                  {{ t('location') }}
                </dt>
                <dd class="text-foreground">{{ event.locationText }}</dd>
              </div>
              <div v-if="event.ownerName">
                <dt class="text-muted-foreground-2 mb-0.5 text-xs font-medium">{{ t('owner') }}</dt>
                <dd class="text-foreground">{{ event.ownerName }}</dd>
              </div>
              <div v-if="event.assistants.length > 0">
                <dt class="text-muted-foreground-2 mb-0.5 text-xs font-medium">
                  {{ t('assistants') }}
                </dt>
                <dd class="text-foreground">{{ event.assistants.join(', ') }}</dd>
              </div>
              <div
                v-if="event.keywords.length > 0"
                class="sm:col-span-2"
              >
                <dt class="text-muted-foreground-2 mb-1 text-xs font-medium">
                  {{ t('keywords') }}
                </dt>
                <dd class="flex flex-wrap gap-1.5">
                  <span
                    v-for="keyword in event.keywords"
                    :key="keyword"
                    class="bg-surface rounded-md px-1.5 py-0.5 text-[11px] text-foreground"
                  >
                    {{ keyword }}
                  </span>
                </dd>
              </div>
              <div
                v-if="internalParticipantNames.length > 0 || externalParticipants.length > 0"
                class="sm:col-span-2"
              >
                <dt class="text-muted-foreground-2 mb-1 text-xs font-medium">
                  {{ t('participants') }}
                </dt>
                <dd class="space-y-1.5 text-foreground">
                  <p v-if="internalParticipantNames.length > 0">
                    {{ internalParticipantNames.join(', ') }}
                  </p>
                  <ul
                    v-if="externalParticipants.length > 0"
                    class="space-y-1"
                  >
                    <li
                      v-for="participant in externalParticipants"
                      :key="participant.email"
                      class="flex flex-wrap items-center gap-2 text-sm"
                    >
                      <span>{{ participantDisplayLabel(participant) || participant.email }}</span>
                      <span
                        v-if="showParticipantRsvp"
                        class="bg-surface text-muted-foreground-1 rounded-md px-1.5 py-0.5 text-[11px]"
                      >
                        {{ rsvpLabel(participant.rsvp_state) }}
                      </span>
                    </li>
                  </ul>
                </dd>
              </div>
              <div
                v-if="event.description"
                class="sm:col-span-2"
              >
                <dt class="text-muted-foreground-2 mb-1 text-xs font-medium">{{ t('notes') }}</dt>
                <dd class="whitespace-pre-wrap text-foreground">{{ event.description }}</dd>
              </div>
            </dl>
          </div>
        </div>
        <footer
          v-if="canEdit"
          class="border-layer-line bg-surface/40 flex items-center justify-between gap-2 border-t px-5 py-3"
        >
          <button
            type="button"
            class="focus:outline-hidden inline-flex items-center gap-x-2 rounded-lg px-3 py-2 text-sm text-rose-500
              hover:bg-rose-500/10"
            :disabled="deleting"
            @click="emit('delete')"
          >
            <svg
              class="size-4 shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M3 6h18" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
            {{ deleting ? '...' : t('cancelEvent') }}
          </button>
          <button
            type="button"
            class="focus:outline-hidden inline-flex items-center gap-x-2 rounded-lg bg-primary px-3 py-2 text-sm
              font-medium text-primary-foreground hover:bg-primary-hover disabled:cursor-not-allowed
              disabled:opacity-60"
            :disabled="deleting"
            @click="requestEdit"
          >
            <svg
              class="size-4 shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
            </svg>
            {{ t('edit') }}
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>
